// src/db/db.ts
import Dexie, { type Table } from 'dexie';

// Import SOLO de tipos (se borra al compilar): la conciliaciÃ³n bancaria necesita
// sus 3 tablas y NO puede existir ciclo en tiempo de ejecuciÃ³n.
import type {
  ReconciliationSession,
  StoredMatch,
  StoredStatementItem,
} from '@/platforms/tools/pages/accounting/bank-reconciliation/types';
// Igual que arriba: tipo puro, sin ciclo en tiempo de ejecuciÃ³n.
import type { BusinessAccountingApiKey } from '@/platforms/tools/pages/accounting/api-access/types';
// Convenios / terceros pagadores: solo tipos; las tablas se crean en v113.
import type {
  AgreementBillingBatch,
  AgreementTransaction,
  ThirdPartyPayer,
} from '@/platforms/tools/pages/agreements/types';
// BÃ³veda legal / compliance: solo tipo; la tabla nace en v114.
import type { LegalDocumentRecord } from '@/platforms/tools/pages/legal-compliance/types';
// Modo proveedor mayorista nativo: perfil, Ã³rdenes B2B y escalas de precio.
import type {
  B2BPurchaseOrder,
  SupplierProfile,
  WholesalePricingTier,
} from '@/platforms/tools/pages/supplier-mode/types';
// Suscripciones de servicios (internet, planes mÃ³viles, plataformas).
import type {
  CustomerSubscription,
  SubscriptionCharge,
  SubscriptionPlan,
} from '@/platforms/tools/pages/subscriptions/types';
import type {
  SaleReturn,
  SaleReturnItem,
  StoreCredit,
} from '@/platforms/tools/pages/returns/types';
import type { TipSettlement } from '@/lib/tipsSettlement';
import type {
  BlindCountAudit,
  BlindCountItem,
} from '@/platforms/tools/pages/blind-count/types';
import type {
  BranchStock,
  InventoryTransfer,
  InventoryTransferItem,
} from '@/platforms/tools/pages/branches/types';
import type { LogisticsCourier, LogisticsJob } from '@/platforms/logistics/types';
import type {
  CourierRequest,
  LogisticsProviderProfile,
} from '@/platforms/logistics/marketplace/types';
import type {
  Assignment,
  AssignmentTask,
} from '@/platforms/tools/pages/assignments/types';
import type { IncentiveAward } from '@/platforms/retail/incentives/types';
import type { DriveFile, DriveFolder } from '@/platforms/tools/pages/drive/types';
import type { PatientDocumentRequest } from '@/platforms/hospitality/public/types';
import type { IndustrialSponsor } from '@/platforms/tools/pages/accounting/industrial-candlesticks/types';
import type { IndustrialSponsorRequest } from '@/platforms/tools/pages/accounting/industrial-candlesticks/types';
import type { AILearningEvent } from '@/lib/local-ai/aiLearningStorage';
import type { FirmClient, FirmClosing } from '@/platforms/accounting-firm/types';
import type {
  AffiliateProfile,
  AffiliateInviteCode,
  AffiliateReferral,
  AffiliateCommission,
  AffiliateWithdrawal,
  AffiliateVerification,
} from '@/platforms/affiliates/types';
import type { ConferenceRoom, ConferenceInvite } from '@/platforms/conference/types';

export interface AuctionEntry {
  id: string;
  auction_id: string;
  user_id: string;
  coins: number;
  paid_at: string;
}
import {
  initInvoiceSequence,
  invoiceNumberValue,
  reserveInvoiceNumber,
} from '@/lib/invoiceSequence';

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// 1. ENUMS Y TIPOS COMUNES
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const UnitTypes = ['UNIDAD', 'KG', 'LB ', 'LT', 'CAJA', 'GR', 'ML', 'METRO', 'PAQUETE', 'DOCENA', 'SERVICIO', 'HORA', 'CM', 'MIN'] as const;
export const ProductTypes = ['FISICO', 'SERVICIO', 'PLATO', 'ESPACIO', 'NO_COTIZABLE'] as const;
export const ProductScopes = ['INTERNO', 'VENTA', 'TIENDA'] as const;
export const PaymentModes = ['SINGLE', 'SPLIT_EQUAL', 'SEPARATE', 'SEPARATE_SHARED'] as const;
export const CancellationReasons = ['ORDER_ERROR', 'CUSTOMER_LEFT', 'OUT_OF_STOCK', 'DELAY', 'OTHER'] as const;
export const CancellationReasonLabels: Record<typeof CancellationReasons[number], string> = {
  ORDER_ERROR: 'Error en el pedido',
  CUSTOMER_LEFT: 'El cliente desistiÃ³',
  OUT_OF_STOCK: 'Producto agotado',
  DELAY: 'Demora',
  OTHER: 'Otro',
};
export const ProductKinds = ['SALE', 'SUPPLY'] as const;
export const TicketDestinations = ['KITCHEN', 'BAR', 'WAREHOUSE', 'NONE'] as const;
export const SaleStatuses = ['COMPLETED', 'PENDING', 'CANCELLED', 'REFUNDED'] as const;
export const OrderStatuses = ['PENDING', 'IN_PROGRESS', 'PREPARING', 'READY', 'DELIVERED'] as const;
export const CashShiftStatuses = ['OPEN', 'CLOSED'] as const;
export const TableStatuses = ['AVAILABLE', 'OCCUPIED', 'RESERVED', 'DIRTY'] as const;
export const AppointmentStatuses = ['PENDING', 'CONFIRMED', 'CHECKED_IN', 'IN_PROGRESS', 'COMPLETED', 'NO_SHOW', 'CANCELLED'] as const;
export const UnitTypesProperty = ['ROOM', 'APARTMENT', 'STORAGE', 'PARKING'] as const;
export const UnitStatusesProperty = ['AVAILABLE', 'OCCUPIED', 'DIRTY', 'MAINTENANCE', 'BLOCKED'] as const;
export const ResourceTypes = ['STAFF', 'ASSET', 'SPACE'] as const;
export const RentalStatuses = ['RESERVED', 'ACTIVE', 'COMPLETED', 'CANCELLED', 'PENDING', 'CONFIRMED', 'RENTED_OUT', 'RETURNED', 'PARTIAL_RETURN', 'OVERDUE', 'DAMAGED'] as const;
export const PlanTypes = ['FREE', 'NARANJA_PLUS', 'ADVANCED_ACCOUNTING', 'STUDIO_FIRM'] as const;
export const SubscriptionStatuses = ['ACTIVE', 'TRIAL', 'PAID', 'EXPIRED', 'CANCELLED'] as const;
export const BetaTypes = ['NONE', 'BETA_TESTER', 'BETA_PRO'] as const;
export const AddonTypes = ['TEMPLATE_STORE', 'TEMPLATE_MENU', 'TEMPLATE_INVOICE', 'MODULE_AI'] as const;

// â”€â”€â”€ NUEVOS ENUMS FINANCIEROS v109 â”€â”€â”€
export const EntryKinds = [
  'SALE', 'EXPENSE', 'TRANSFER', 'PAYROLL', 'DEBT',
  'COLLECTION', 'ADJUSTMENT', 'REVERSAL', 'TAX', 'OPENING'
] as const;
export const AccountClasses = [
  'CASH_BOX', 'RECEIVABLE', 'PAYABLE', 'TAX_PAYABLE',
  'EQUITY', 'INCOME', 'EXPENSE', 'INVENTORY'
] as const;
export const BoxRoles = [
  'TAX', 'PROFIT', 'BUFFER', 'OPERATING', 'REINVESTMENT', 'CUSTOM'
] as const;
export const BoxTypes = ['MASTER', 'CHILD'] as const;
export const AssemblyStatuses = ['DRAFT', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const;
export const AffiliateStatuses = ['PENDING', 'ACTIVE', 'SUSPENDED', 'TERMINATED'] as const;
export const TelemetryBucketTypes = [
  'DAILY', 'WEEKLY', 'MONTHLY', 'HOURLY'
] as const;
export const CashBoxStatuses = ['ACTIVE', 'INACTIVE', 'FROZEN'] as const;

// â”€â”€â”€ NUEVOS ENUMS v110 (Calendario y Lifecycle) â”€â”€â”€
export const WorkShiftStatuses = ['SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const;
export const LifecycleJobStatuses = ['PENDING', 'RUNNING', 'COMPLETED', 'FAILED'] as const;

export type UnitType = typeof UnitTypes[number];
export type ProductType = typeof ProductTypes[number];
export type ProductScope = typeof ProductScopes[number];
export type ProductKind = typeof ProductKinds[number];
export type PaymentMode = typeof PaymentModes[number];
export type CancellationReason = typeof CancellationReasons[number];
export type SaleItemStatus = 'ACTIVE' | 'CANCELLED';
export type DinerId = string;
export type TableId = number | string;
export type TicketDestination = typeof TicketDestinations[number];
export type SaleStatus = typeof SaleStatuses[number];
export type OrderStatus = typeof OrderStatuses[number];
export type CashShiftStatus = typeof CashShiftStatuses[number];
export type TableStatus = typeof TableStatuses[number];
export type AppointmentStatus = typeof AppointmentStatuses[number];
export type PropertyUnitType = typeof UnitTypesProperty[number];
export type PropertyUnitStatus = typeof UnitStatusesProperty[number];
export type ResourceType = typeof ResourceTypes[number];
export type RentalStatus = typeof RentalStatuses[number];
export type PlanType = typeof PlanTypes[number];
export type SubscriptionStatus = typeof SubscriptionStatuses[number];
export type BetaType = typeof BetaTypes[number];
export type AddonType = typeof AddonTypes[number];

export type EntryKind = typeof EntryKinds[number];
export type AccountClass = typeof AccountClasses[number];
export type BoxRole = typeof BoxRoles[number];
export type BoxType = typeof BoxTypes[number];
export type AssemblyStatus = typeof AssemblyStatuses[number];
export type AffiliateStatus = typeof AffiliateStatuses[number];
export type TelemetryBucketType = typeof TelemetryBucketTypes[number];
export type CashBoxStatus = typeof CashBoxStatuses[number];

export type WorkShiftStatus = typeof WorkShiftStatuses[number];
export type LifecycleJobStatus = typeof LifecycleJobStatuses[number];

export const FLIP_COIN_VALUE_COP = 100;
export type BusinessType = 'RETAIL' | 'RESTAURANT' | 'PROPERTY' | 'RENTAL' | 'HOSPITALITY' | 'LOGISTICS' | 'ACCOUNTING_FIRM';

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// 2. MODELOS PRINCIPALES
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export interface Ingredient {
  product_id: string;
  product_name: string;
  quantity: number;
  unit: UnitType;
  cost_ratio?: number;
}

export interface ItemModifier {
  type: 'ADD' | 'REMOVE' | 'NOTE';
  value: string;
  price_impact?: number;
}

export interface SystemTemplate {
  id: string;
  name: string;
  description: string;
  price: number;
  target_type: 'SUPPLIER' | 'BUSINESS' | 'BOTH';
  preview_url?: string;
  payment_link?: string;
}

export interface Budget {
  id?: string;
  business_id: string;
  month: string;
  category: string;
  amount: number;
  target_amount: number;
  spent: number;
  currency?: string;
}

export interface RecurringExpense {
  id?: string;
  business_id: string;
  name: string;
  amount: number;
  category: string;
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
  day_of_payment?: number;
  active: boolean;
  currency?: string;
}

export interface Debt {
  id?: string;
  business_id: string;
  name: string;
  total_amount: number;
  paid_amount: number;
  category: string;
  due_date?: string;
  notes?: string;
  status: 'ACTIVE' | 'PAID';
  currency?: string;
  created_at: string;
  updated_at: string;
}

export interface TaxPayment {
  id?: string;
  business_id: string;
  date: string;
  amount: number;
  period: string;
  status: 'PENDING' | 'PAID';
  currency?: string;
}

export interface AIChatSession {
  id?: string;
  business_id: string;
  title: string;
  updated_at: string;
  context_type?: string;
}

export interface AIChatMessage {
  id?: string;
  session_id: string;
  role: 'user' | 'model';
  content: string;
  created_at: string;
}

export interface InstalledAddon {
  id?: string;
  business_id: string;
  addon_type: AddonType;
  is_active: boolean;
  settings?: any;
}

export interface Business {
  id: string;
  name: string;
  type: BusinessType;
  owner_id: string;
  plan: PlanType;
  slug?: string;
  active_template_id?: string;
  address?: string;
  phone?: string;
  email?: string;
  tax_id?: string;
  currency?: string;
  timezone?: string;
  created_at: string;
  updated_at: string;
  supabase_id?: string | null;
}

export type SalaryType = 'HOURLY' | 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'ON_DEMAND';

export interface UserProfile {
  id: string;
  business_id: string;
  full_name: string;
  email?: string;
  password?: string;
  pin: string;
  role: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  document_number?: string;
  phone?: string;
  hire_date?: string;
  salary_type?: SalaryType;
  salary_amount?: number;
  schedule?: string;
  schedule_days?: string[];
  schedule_start?: string;
  schedule_end?: string;
  schedule_effective_at?: string | null;
  contract_type?: string;
  tips_accumulated?: number;
}

export interface StaffChange {
  id: string;
  business_id: string;
  user_id: string;
  user_name: string;
  change_type: 'HORARIO' | 'SUELDO' | 'ROL' | 'CONTRATO';
  previous_value?: string | null;
  new_value?: string | null;
  details?: string | null;
  effective_at: string;
  created_at: string;
  created_by?: string | null;
  seen_at?: string | null;
  sync_status?: 'SYNCED' | 'LOCAL' | undefined;
}

export interface Product {
  id?: string;
  business_id: string;
  name: string;
  description?: string;
  /**
   * Marca comercial del producto (opcional). Aditivo: ausente === sin marca.
   * No estÃ¡ indexado, asÃ­ que no requiere migraciÃ³n de esquema Dexie.
   */
  brand?: string | null;
  price: number;
  cost: number | null;
  sku?: string | null;
  barcode?: string | null;
  stock_current: number;
  stock_min: number;
  unit: UnitType;
  type: ProductType;
  category_id?: number | null;
  default_supplier_id?: string;
  is_active: boolean;
  default_destination: TicketDestination;
  /** âš¡ Venta ultrarrÃ¡pida: la tarjeta del POS ofrece botones de cobro a 1 clic. */
  quick_sale?: boolean;
  /**
   * MÃ©todo de cobro de la venta ultrarrÃ¡pida.
   * - `CASH`: solo efectivo.
   * - `TRANSFER`: solo transferencia/QR (llave bancaria activa).
   * - `DUAL`: muestra ambos botones (ðŸ’µ y ðŸ“±).
   *
   * Campo NO indexado: no requiere una nueva versiÃ³n de Dexie. Las filas
   * antiguas (`undefined`) se comportan como `CASH` por compatibilidad.
   */
  quick_sale_payment_method?: QuickSalePaymentMethod;
  recipe?: Ingredient[];
  image_url?: string | null;
  expiration_date?: string | null;
  created_at?: string;
  updated_at?: string;
  supabase_id?: string | null;
  price_bulk?: number;
  bulk_min_qty?: number;
  /** ðŸ­ Venta mayorista B2B: usa el MISMO producto del inventario. */
  is_b2b_wholesale?: boolean;
  wholesale_price?: number;
  min_wholesale_qty?: number;
  /** Escalas opcionales por cantidad (precio unitario exacto por tramo). */
  wholesale_tiers?: WholesalePricingTier[];
  commission_type?: 'UNIT' | 'PERCENTAGE';
  supplier_category?: string;
  supplier_name?: string;
  product_status_note?: string;
  is_sponsored?: boolean;
  sponsored_until?: string | null;
  product_scopes?: ProductScope[];
  product_scope?: ProductScope | ProductScope[];
  unit_base?: UnitType;
  conversion_to?: UnitType;
  conversion_factor?: number;
  price_variants?: { label: string; gramaje?: number; price: number }[];
  product_kind?: ProductKind;
  catalog?: string;
  catalog_custom?: string | null;
  equipment_ids?: string[];

  // â”€â”€â”€ NUEVOS CAMPOS FISCALES Y DE COSTO v109 â”€â”€â”€
  /** Tarifa de impuesto aplicable en porcentaje (ej: 19 = 19%). */
  tax_rate?: number;
  /** Indica si el precio ya incluye el impuesto. */
  tax_included?: boolean;
  /** Costo landed real (incluye fletes, seguros, aranceles) en moneda base. */
  landed_cost?: number;
  /** Tasa de merma tÃ©cnica normal en porcentaje (ej: 2 = 2%). */
  normal_shrinkage_rate?: number;
  /** Etiquetas dinÃ¡micas para clasificaciÃ³n flexible. */
  tags?: string[];

  // â”€â”€â”€ NUEVOS CAMPOS DE CONVERSIÃ“N CAJA/UNIDAD v110 â”€â”€â”€
  /** Precio por caja (si el producto se vende por cajas). */
  box_price?: number;
  /** CuÃ¡ntas unidades hay en una caja (ej: 12 unidades por caja). */
  units_per_box?: number;
  /** Costo total del empaque/caja (se reparte entre `units_per_box`). */
  box_cost?: number;
}

// â”€â”€â”€ âš¡ VENTA ULTRARRÃPIDA â”€â”€â”€
export const QuickSalePaymentMethods = ['CASH', 'TRANSFER', 'DUAL'] as const;
export type QuickSalePaymentMethod = (typeof QuickSalePaymentMethods)[number];

/** MÃ©todos que se ejecutan con un clic real (DUAL se resuelve en dos botones). */
export type QuickSaleChargeMethod = Extract<QuickSalePaymentMethod, 'CASH' | 'TRANSFER'>;

export const QuickSalePaymentMethodLabels: Record<QuickSalePaymentMethod, string> = {
  CASH: 'Solo efectivo',
  TRANSFER: 'Solo transferencia / QR',
  DUAL: 'Ambos (doble botÃ³n)',
};

/** Guard de runtime para valores que llegan de formularios o de la nube. */
export const isQuickSalePaymentMethod = (value: unknown): value is QuickSalePaymentMethod =>
  value === 'CASH' || value === 'TRANSFER' || value === 'DUAL';

/** Etiquetas cortas para los botones del POS. */
export const QuickSaleChargeMethodLabels: Record<QuickSaleChargeMethod, string> = {
  CASH: 'Efectivo',
  TRANSFER: 'Transferencia',
};

/**
 * Normaliza el mÃ©todo de cobro guardado. Cualquier valor ausente o invÃ¡lido
 * cae en `CASH` para no romper productos creados antes de esta funciÃ³n.
 */
export const getQuickSalePaymentMethod = (
  p: Partial<Pick<Product, 'quick_sale_payment_method'>>,
): QuickSalePaymentMethod => {
  const raw = p.quick_sale_payment_method;
  return raw === 'TRANSFER' || raw === 'DUAL' || raw === 'CASH' ? raw : 'CASH';
};

/**
 * MÃ©todos que debe ofrecer la tarjeta del POS.
 * Devuelve [] si el producto no estÃ¡ marcado como venta ultrarrÃ¡pida.
 */
export const getQuickSaleChargeMethods = (p: Partial<Product>): QuickSaleChargeMethod[] => {
  if (p.quick_sale !== true) return [];
  switch (getQuickSalePaymentMethod(p)) {
    case 'TRANSFER':
      return ['TRANSFER'];
    case 'DUAL':
      return ['CASH', 'TRANSFER'];
    case 'CASH':
    default:
      return ['CASH'];
  }
};

// â”€â”€â”€ âš¡ MODO DE COBRO GLOBAL (fuera de la venta ultrarrÃ¡pida) â”€â”€â”€
export const QuickSalePaymentModes = ['CASH', 'TRANSFER', 'ASK'] as const;
export type QuickSalePaymentMode = (typeof QuickSalePaymentModes)[number];

export const QuickSalePaymentModeLabels: Record<QuickSalePaymentMode, string> = {
  CASH: 'Solo efectivo',
  TRANSFER: 'Solo transferencia',
  ASK: 'Preguntar',
};

export const isQuickSalePaymentMode = (value: unknown): value is QuickSalePaymentMode =>
  value === 'CASH' || value === 'TRANSFER' || value === 'ASK';

export const getProductScopes = (p: Partial<Product>): ProductScope[] => {
  if (p.product_scopes && p.product_scopes.length > 0) return [...p.product_scopes];
  const legacy = p.product_scope;
  if (!legacy) return ['VENTA'];
  if (Array.isArray(legacy)) return [...legacy];
  return [legacy];
};

export const getProductKind = (p: Partial<Product>): ProductKind =>
  p.product_kind === 'SUPPLY' ? 'SUPPLY' : 'SALE';

export const isSupply = (p: Partial<Product>): boolean => getProductKind(p) === 'SUPPLY';

export const isVisibleIn = (p: Partial<Product>, scope: ProductScope): boolean => {
  if (scope !== 'INTERNO' && isSupply(p)) return false;
  return getProductScopes(p).includes(scope);
};

export interface InventoryLog {
  id: string;
  business_id: string;
  product_id: string;
  product_name: string;
  change_amount: number;
  final_stock: number;
  reason: 'RESTOCK' | 'SALE' | 'CORRECTION' | 'DAMAGE';
  user_name: string;
  created_at: string;
  /** Datos ampliados para auditorÃ­a de ajustes y compras. */
  operation?: 'RESTOCK' | 'CORRECTION' | 'DAMAGE' | 'REMOVE';
  notes?: string;
  supplier_name?: string;
  previous_stock?: number;
  unit_cost?: number;
  total_value?: number;
  tax_included?: boolean;
  tax_amount?: number;
}

export interface FulfillmentRequest {
  id: string;
  business_id: string;
  customer_name: string;
  items_json: string;
  total: number;
  status: 'PENDING' | 'READY' | 'COMPLETED';
  destination: 'WAREHOUSE' | 'KITCHEN' | 'BAR';
  created_at: string;
  updated_at: string;
}

export interface StoreOrder {
  id: string;
  business_id: string;
  customer_name: string;
  customer_phone?: string;
  customer_address?: string;
  customer_notes?: string;
  delivery_type: 'pickup' | 'delivery';
  payment_method: 'cash' | 'transfer';
  items_json: string;
  total: number;
  status: 'PENDING' | 'CONFIRMED' | 'REJECTED' | 'COMPLETED';
  created_at: string;
  is_read: number;
}

export interface NiizNotification {
  id: string;
  business_id: string;
  title: string;
  message: string;
  type: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR' | 'SALE' | 'ORDER';
  link?: string;
  created_at: string;
  is_read: number;
}

export interface Supplier {
  id: string;
  name: string;
  business_name: string;
  rut: string;
  logo_url?: string;
  whatsapp: string;
  address: string;
  city: string;
  password?: string;
  email?: string;
  user_id?: string;
  is_verified: number;
  verification_code?: string;
  opening_time?: string;
  closing_time?: string;
  description?: string;
  category?: string;
  slug?: string;
  active_template_id?: string;
  linked_business_id?: string;
  membership_type: 'BASIC' | 'ADS' | 'PREMIUM' | 'REGULAR';
  membership_base_fee: number;
  is_ads?: boolean;
  ads_expiry?: string;
  wallet_balance?: number;
  wallet_limit?: number;
  default_count: number;
  rating: number;
  created_at: string;
}

export interface FinancialCertificate {
  id: string;
  business_id: string;
  verification_token: string;
  metrics_snapshot: any;
  business_name?: string | null;
  nit?: string | null;
  owner_name?: string | null;
  city?: string | null;
  health_index?: number | null;
  stability_index?: number | null;
  coverage_days?: number | null;
  charged_coins?: number | null;
  was_free?: boolean | null;
  naranja_active?: boolean | null;
  revoked?: boolean | null;
  created_at: string;
  sync_status?: 'SYNCED' | 'LOCAL' | undefined;
}

export interface NaranjaPlus {
  id: string;
  business_id: string;
  status: 'ACTIVE' | 'INACTIVE' | 'GRACE_PERIOD';
  expires_at: string | null;
  monthly_price_cop: number;
  auto_renew: boolean;
  activated_at?: string | null;
  last_charge_at?: string | null;
  last_charge_coins?: number | null;
  cancel_reason?: string | null;
  created_at: string;
  updated_at?: string | null;
  sync_status?: 'SYNCED' | 'LOCAL' | undefined;
}

export interface CategoryAuction {
  id: string;
  category: string;
  city: string;
  month_year: string;
  starting_bid?: number | null;
  current_highest_bid?: number | null;
  leader_supplier_id?: string | null;
  leader_supplier_name?: string | null;
  leader_user_id?: string | null;
  ends_at: string;
  status: 'OPEN' | 'CLOSED';
  winner_supplier_id?: string | null;
  winner_supplier_name?: string | null;
  winner_price?: number | null;
  closed_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  sync_status?: 'SYNCED' | 'LOCAL' | undefined;
}

export interface AdBanner {
  id: string;
  supplier_id?: string | null;
  user_id?: string | null;
  title?: string | null;
  image_url: string;
  link_url?: string | null;
  product_name?: string | null;
  city?: string | null;
  is_active?: boolean | null;
  starts_at?: string | null;
  ends_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  sync_status?: 'SYNCED' | 'LOCAL' | undefined;
}

export interface SupplierProduct {
  id: string;
  supplier_id: string;
  category: string;
  name: string;
  description?: string;
  image_url?: string;
  price_unit: number;
  price_bulk: number;
  bulk_min_qty: number;
  commission_type: 'UNIT' | 'PERCENTAGE';
  stock: number;
  is_active: number;
  business_id?: string;
  user_id?: string;
  supplier_name?: string;
  updated_at?: string;
}

export interface MarketplaceOrder {
  id: string;
  supplier_id: string;
  business_id: string;
  business_name?: string;
  customer_name?: string;
  customer_address?: string;
  customer_phone?: string;
  customer_rut?: string;
  customer_notes?: string;
  items_json: string;
  total_value: number;
  platform_commission: number;
  supplier_net: number;
  status: 'PENDING' | 'CONFIRMED' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED';
  payment_method: 'CASH' | 'TRANSFER';
  payment_status: 'PENDING' | 'PAID' | 'FAILED';
  currency?: string;
  created_at: string;
}

export interface SavedDocument {
  id: string;
  business_id: string;
  name: string;
  content_html: string;
  footer?: string;
  footer_align?: 'left' | 'center' | 'right';
  page_size?: string;
  created_at: string;
  updated_at: string;
}

export interface SavedSpreadsheet {
  id: string;
  business_id: string;
  name: string;
  sheets_json: string;
  active_sheet?: number;
  created_at: string;
  updated_at: string;
}

export interface SupportRequest {
  id: string;
  business_id: string;
  business_name: string;
  subject: string;
  message: string;
  request_type?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'IN_PROGRESS' | 'RESOLVED';
  created_at: string;
  resolved_at?: string;
  seen_by_business?: number;
}

export interface Sale {
  id?: string;
  business_id: string;
  user_id: string;
  user_name: string;
  customer_id?: string | null;
  total: number;
  subtotal: number;
  tax: number;
  discount: number;
  service_charge?: number;
  payment_method: string;
  status: SaleStatus;
  amount_received: number;
  change_due: number;
  payments_detail?: string;
  table_id?: number | null;
  order_type?: 'DINE_IN' | 'TAKE_AWAY' | 'DELIVERY' | null;
  created_at: string;
  synced: boolean;
  supabase_id?: string | null;
  /** Factura unificada: FAC-0001, FAC-0002â€¦ atada a la resoluciÃ³n DIAN. */
  invoice_number?: string | null;
  invoice_resolution_id?: string | null;
  payment_mode?: PaymentMode | null;
  diners?: Diner[];
  split_count?: number | null;
  settlements?: SaleSettlement[];
  separate_invoices?: boolean | null;
  cancelled_at?: string | null;
  cancel_reason?: CancellationReason | null;
  cancel_reason_label?: string | null;
  cancel_notes?: string | null;
  cancelled_by?: string | null;
  currency?: string;

  // â”€â”€â”€ âš¡ VENTA ULTRARRÃPIDA GLOBAL (monto libre) â”€â”€â”€
  /** `true` si la venta se creÃ³ por monto libre desde el Header global. */
  is_quick_amount_sale?: boolean;
  /** `true` mientras los productos/stock aÃºn no se han desglosado. */
  pending_itemization?: boolean;
  /** Nota de voz del cajero (data URL persistente, no un blob temporal). */
  audio_note_url?: string | null;
  audio_note_mime_type?: string | null;
  audio_note_duration_ms?: number | null;
  /** Canal de ingreso: caja fÃ­sica o banco/transferencia. */
  quick_sale_channel?: 'CASH' | 'BANK';
  quick_sale_cash_box_id?: string | null;
  quick_sale_cash_box_name?: string | null;
  /** Llave bancaria activa usada cuando el cobro fue por transferencia. */
  payment_key_id?: string | null;
  /** Marca temporal del desglose posterior. */
  itemized_at?: string | null;
  itemized_by?: string | null;
  itemization_total?: number | null;
  itemization_difference?: number | null;
  itemization_notes?: string | null;
}

export interface Diner {
  id: DinerId;
  name: string;
  origin_table_id?: TableId | null;
  paid?: boolean;
  paid_amount?: number;
  paid_at?: string | null;
  payment_method?: string | null;
}

export interface SaleSettlement {
  bucket_id: string;
  label: string;
  amount: number;
  paid: boolean;
  paid_at?: string | null;
  payment_method?: string | null;
  payments_detail?: string | null;
}

export interface SaleItem {
  id?: string;
  sale_id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes?: string | null;
  ticket_destination: TicketDestination;
  origin_table_id?: TableId | null;
  diner_id?: DinerId | null;
  is_shared?: boolean;
  shared_diner_ids?: DinerId[] | null;
  status?: SaleItemStatus;
  cancelled_at?: string | null;
  cancelled_by?: string | null;
  cancelled_by_id?: string | null;
  cancel_reason?: CancellationReason | null;
  cancel_reason_label?: string | null;
  cancel_notes?: string | null;
  cancel_was_prepared?: boolean;
  cancelled_quantity?: number | null;
  cancelled_amount?: number | null;
  item_note?: string | null;
}

export interface CashShift {
  id?: string;
  business_id: string;
  status: CashShiftStatus;
  opened_at: string;
  closed_at?: string | null;
  opened_by: string;
  closed_by?: string | null;
  initial_amount: number;
  final_amount?: number | null;
  notes?: string | null;
  synced?: boolean;
  sync_deferred_reason?: string | null;
  total_tips?: number;
  tips_breakdown?: { user_id: string; name: string; role?: string; amount: number }[] | null;
  currency?: string;
}

export type SyncQueueEntity = 'sale' | 'sale_item' | 'expense' | 'cash_shift';

export interface SyncQueueItem {
  id: string;
  business_id: string;
  entity: SyncQueueEntity;
  entity_id: string;
  status: 'PENDING' | 'SYNCED';
  reason?: string | null | undefined;
  attempts: number;
  created_at: string;
  updated_at?: string | null | undefined;
}

export interface TableConfig {
  id?: number;
  business_id: string;
  name: string;
  zone: string;
  capacity: number;
  status: TableStatus;
  current_sale_id?: string | null;
  x_position?: number;
  y_position?: number;
  parent_table_id?: TableId | null;
  joined_at?: string | null;
}

export interface Customer {
  id?: string;
  business_id: string;
  full_name: string;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  tax_id?: string | null;
  credit_limit?: number | null;
  current_debt: number;
  is_active: boolean;
  created_at?: string;
}

export interface OrderTicket {
  id?: string;
  sale_id: string;
  table_id?: number | null;
  destination: TicketDestination;
  status: OrderStatus;
  items_detail: any[];
  created_at: string;
  created_by: string;
  table_name?: string;
  customer_name?: string;
  prepared_by?: string;
  cancelled?: boolean;
  partial_cancel?: boolean;
  cancelled_items?: string[];
  cancelled_at?: string | null;
  cancelled_by?: string | null;
  cancel_reason?: string | null;
  cancel_notes?: string | null;
  was_prepared?: boolean;
  ticket_notes?: string | null;
}

export interface ServiceMaterial {
  product_id: string;
  product_name: string;
  quantity: number;
  unit?: string;
}

export interface Service {
  id: string;
  name: string;
  duration_minutes: number;
  price: number;
  is_active: boolean;
  category?: string;
  business_id?: string;
  materials?: ServiceMaterial[];
  equipment_ids?: string[];
}

export interface Resource {
  id: string;
  name: string;
  type: 'STAFF' | 'SPACE' | 'ASSET';
  is_active: boolean;
  status: 'AVAILABLE' | 'BUSY' | 'AWAY' | 'MAINTENANCE';
  business_id?: string;
  brand?: string;
  model?: string;
  serial?: string;
  purchase_date?: string;
  maintenance_interval_days?: number;
  last_maintenance_at?: string;
  next_maintenance_at?: string;
  notes?: string;
}

export interface Appointment {
  id: string;
  resource_id: string;
  customer_id?: string;
  customer_name: string;
  service_id: string;
  service_name: string;
  service_price: number;
  start_time: string;
  end_time: string;
  status: 'PENDING' | 'CONFIRMED' | 'CHECKED_IN' | 'IN_PROGRESS' | 'COMPLETED' | 'NO_SHOW' | 'CANCELLED';
  is_paid: boolean;
  notes?: string;
  appointment_type?: 'ARRIVAL' | 'RESERVATION';
  arrival_number?: number;
}

export interface PropertyUnit {
  id?: string;
  business_id: string;
  name: string;
  type: PropertyUnitType;
  base_price: number;
  price_period: 'NIGHTLY' | 'WEEKLY' | 'MONTHLY';
  capacity_adults: number;
  capacity_children: number;
  features: string[];
  status: PropertyUnitStatus;
  floor?: string;
  internal_notes?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface PropertyBooking {
  id?: string;
  business_id: string;
  unit_id: string;
  customer_id: string;
  customer_name: string;
  check_in_date: string;
  check_out_date: string;
  nights_count: number;
  adults_count: number;
  children_count: number;
  total_price: number;
  deposit_amount?: number;
  is_paid: boolean;
  payment_method?: string;
  status: AppointmentStatus;
  guest_notes?: string;
  internal_notes?: string;
  actual_check_in?: string;
  actual_check_out?: string;
  created_at?: string;
  updated_at?: string;
}

export interface HousekeepingTask {
  id?: string;
  business_id: string;
  unit_id: string;
  unit_name: string;
  type: 'CLEANING' | 'DEEP_CLEANING' | 'REPAIR' | 'INSPECTION' | 'OTHER';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  description: string;
  assigned_to?: string;
  due_date?: string;
  completed_at?: string;
  notes?: string;
  created_at: string;
  created_by: string;
}

export interface RentalItem {
  id?: string;
  business_id: string;
  name: string;
  description?: string;
  sku?: string;
  total_stock: number;
  pricing: {
    HOURLY?: number;
    DAILY: number;
    WEEKEND?: number;
    WEEKLY?: number;
  };
  deposit_amount?: number;
  category: string;
  subcategory?: string;
  image_urls: string[];
  thumbnail_url?: string;
  requires_inspection: boolean;
  maintenance_interval_days?: number;
  is_active: boolean;
  price_per_day?: number;
  active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface RentalBooking {
  id?: string;
  business_id: string;
  customer_id: string;
  customer_name: string;
  customer_phone: string;
  customer_email?: string;
  items: {
    item_id: string;
    item_name: string;
    quantity: number;
    unit_price: number;
    subtotal: number;
    notes?: string;
  }[];
  start_datetime: string;
  end_datetime: string;
  actual_return_datetime?: string;
  status: RentalStatus;
  subtotal: number;
  total_price: number;
  deposit_amount: number;
  deposit_returned: number;
  total_paid: number;
  payment_method?: string;
  notes?: string;
  damage_notes?: string;
  rented_out_at?: string;
  returned_at?: string;
  created_at?: string;
  updated_at?: string;
  created_by: string;
}

export interface TelemetrySaleItem {
  product_name: string;
  category: string;
  product_type: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

export interface TelemetryEvent {
  id: string;
  business_hash: string;
  business_type?: string;
  sale_id: string;
  occurred_at: string;
  day_of_week: number;
  month: number;
  hour: number;
  is_weekend: boolean;
  order_type?: string;
  payment_method?: string;
  total_amount: number;
  item_count: number;
  items: TelemetrySaleItem[];
  uploaded: number;
  created_at: string;
}

export interface FlipTransaction {
  id: string;
  type: 'RECHARGE' | 'CHARGE' | 'SERVICE';
  description: string;
  amount: number;
  balance_after: number;
  created_at: string;
}

export interface FlipPendingRecharge {
  id: string;
  business_id: string;
  customer_label: string;
  cop_amount: number;
  coins: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  created_at: string;
  resolved_at?: string;
}

export interface Device {
  id: string;
  business_id: string;
  name: string;
  terminal_type: string;
  role?: string;
  /** Sede fÃ­sica a la que pertenece este computador/terminal. */
  branch_id?: string;
  last_seen_at?: string;
  is_active: boolean;
  created_at: string;
}

export interface EmployeeSession {
  id: string;
  user_id: string;
  user_name: string;
  device_id?: string;
  device_name?: string;
  business_id: string;
  login_at: string;
  logout_at?: string;
}

export interface SecurityQuery {
  id: string;
  entity_name: string;
  entity_employee_id: string;
  queried_document: string;
  queried_at: string;
  paid: boolean;
}

export interface Subasta {
  id: string;
  product_name: string;
  canonical_name?: string;
  category?: string;
  status: 'SCHEDULED' | 'OPEN' | 'CLOSED';
  buyers: number;
  total_quantity: number;
  standard_price: number;
  market_price: number;
  commission_cop: number;
  winner_supplier_id?: string;
  winner_price?: number;
  ends_at: string;
  bid_deadline?: string;
  starts_at: string;
  created_at: string;
  sync_status?: 'SYNCED' | 'LOCAL';
  buyers_detail?: any[];
}

export interface SubastaBid {
  id: string;
  subasta_id: string;
  supplier_id: string;
  supplier_name: string;
  price: number;
  created_at: string;
}

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  cover_image_url?: string;
  author?: string;
  tags?: string[];
  status: 'DRAFT' | 'PUBLISHED';
  created_at: string;
  updated_at: string;
}

export const PaymentKeyTypes = ['CELULAR', 'DOCUMENTO_NIT', 'CORREO', 'NUMERO_CUENTA', 'ALFANUMERICA'] as const;
export type PaymentKeyType = typeof PaymentKeyTypes[number];
export const PaymentKeyTypeLabels: Record<PaymentKeyType, string> = {
  CELULAR: 'Celular',
  DOCUMENTO_NIT: 'Documento / NIT',
  CORREO: 'Correo',
  NUMERO_CUENTA: 'NÃºmero de cuenta',
  ALFANUMERICA: 'AlfanumÃ©rica (Bre-B)',
};

export interface PaymentKey {
  id: string;
  business_id: string;
  bank_name: string;
  key_type: PaymentKeyType;
  key_value: string;
  holder_name: string;
  qr_image: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at?: string | null;
}

export interface Expense {
  id: string;
  business_id: string;
  date: string;
  category: string;
  amount: number;
  description?: string;
  user_id?: string;
  user_name?: string;
  payment_method?: string;
  cash_box_id?: string;
  currency?: string;
  tax_amount?: number;
  tax_rate?: number;
  tax_label?: string;
  source_box?: string;
  cost_center_id?: string;
  branch_id?: string;
  tags?: string[];
  synced?: boolean;
  created_at: string;
  updated_at?: string;
}

export interface AppSettings {
  id: string;
  business_id: string;
  created_at: string;
  business_name: string;
  business_type: string;
  currency_symbol: string;
  tax_rate: number;
  address?: string;
  country?: string;
  city?: string;
  phone?: string;
  logo_url?: string | null;
  bank_accounts?: any[];
  footer_message?: string;
  tax_id?: string;
  admin_pin?: string;
  plan_type: PlanType;
  subscription_status: SubscriptionStatus;
  /** Modo Hogar: permite registrar retiros personales del negocio. */
  home_mode_enabled?: boolean;
  subscription_expiry?: string;
  /** Suscripción NIIZ: fechas y renovación automática. */
  plan_started_at?: string;
  plan_expires_at?: string;
  auto_renew?: boolean;
  grace_period_started_at?: string | null;
  /** Contador de consultas a IA local para el cobro 1/10. */
  ai_local_counter?: number;
  /** Consultas de IA local hechas sin conexión, pendientes de sincronizar. */
  ai_local_pending?: number;
  /** Franja de nube incluida (MB) y GB extra contratados. */
  cloud_included_mb?: number;
  cloud_extra_gb?: number;
  beta_type: BetaType;
  ai_enabled: boolean;
  marketplace_enabled: boolean;
  developer_mode: boolean;
  gemini_api_key?: string;
  deepseek_api_key?: string;
  telemetry_enabled?: boolean;
  telemetry_last_upload?: string;
  flip_balance?: number;
  credit_value_cop?: number;
  ai_cost_per_1000_tokens?: number;
  ai_cost_margin_percent?: number;
  ai_costs?: Record<string, number>;
  wompi_5000?: string;
  wompi_10000?: string;
  wompi_20000?: string;
  wompi_80000?: string;
  wompi_whatsapp?: string;
  wompi_payment_link?: string;
  bre_b_key_type?: string;
  bre_b_key?: string;
  bre_b_holder?: string;
  bre_b_gui?: string;
  bre_b_mcc?: string;
  bre_b_enabled?: boolean;
  /**
   * âš¡ Venta ultrarrÃ¡pida: envÃ­a el ticket a la ticketera automÃ¡ticamente al
   * cobrar con 1 clic. Si estÃ¡ apagado, la pantalla queda limpia al instante.
   * Campo NO indexado: no requiere una nueva versiÃ³n de Dexie.
   */
  quick_sale_auto_print?: boolean;
  /**
   * âš¡ Venta ultrarrÃ¡pida: muestra el modal de cambio/descuento antes de
   * cobrar. Si estÃ¡ apagado, el cobro es directo en 1 clic.
   * Campo NO indexado: no requiere una nueva versiÃ³n de Dexie.
   */
  quick_sale_show_change_modal?: boolean;
  /**
   * âš¡ MÃ©todo de cobro GLOBAL para ventas fuera de la venta ultrarrÃ¡pida.
   * - `CASH`: cobra directo en efectivo.
   * - `TRANSFER`: cobra directo por transferencia/QR.
   * - `ASK`: pregunta efectivo o transferencia antes de cobrar.
   */
  quick_sale_payment_mode?: QuickSalePaymentMode;
  /** Atajos configurables: id de acciÃ³n â†’ combinaciÃ³n (F2, Ctrl+K, Alt+Pâ€¦). */
  keyboard_shortcuts?: Record<string, string>;
  cloud_enabled?: boolean;
  cloud_monthly_credit?: number;
  cloud_usage_mb?: number;
  cloud_cost_per_gb_cop?: number;
  cloud_margin_per_gb_cop?: number;
  is_supplier?: boolean;
  terms_accepted?: boolean;
  terms_accepted_at?: string | null;
  terms_version?: string | null;
  beta_last_granted_at?: string;
  beta_coins_granted?: boolean;
  store_enabled?: boolean;
  store_name?: string;
  store_slug?: string;
  store_description?: string;
  store_whatsapp?: string;
  store_banner_color?: string;
  store_active_template?: string;
  store_ai_enabled?: boolean;
  store_schedule?: {
    enabled: boolean;
    days: Record<string, { open: string; close: string; active: boolean }>;
  };
  currency_code?: string;
  currency_symbol_display?: string;
  decimal_separator?: string;
  thousand_separator?: string;
}

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// 3. MODELOS FINANCIEROS NUEVOS v109
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export interface Branch {
  id: string;
  business_id: string;
  name: string;
  code: string;
  address?: string;
  city?: string;
  phone?: string;
  is_main: boolean;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  tags?: string[];
}

export interface CostCenter {
  id: string;
  business_id: string;
  branch_id: string;
  code: string;
  name: string;
  description?: string;
  manager?: string;
  default_box?: string;
  budget: number;
  currency?: string;
  tags?: string[];
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface AdminCashBox {
  id: string;
  business_id: string;
  branch_id?: string;
  parent_id?: string;
  type: BoxType;
  role: BoxRole;
  name: string;
  percentage: number;
  balance: number;
  floor?: number;
  currency?: string;
  cap_table?: { owner_id: string; name: string; share_bps: number }[];
  can_donate_to?: string[];
  tags?: string[];
  status: CashBoxStatus;
  created_at: string;
  updated_at?: string;
}

export interface AccountingEntry {
  id: string;
  business_id: string;
  branch_id?: string;
  number: string;
  date: string;
  posted_at: string;
  kind: EntryKind;
  postings_json: string;
  description: string;
  status: 'DRAFT' | 'POSTED' | 'VOID';
  caused_by: string[];
  reverses?: string;
  idempotency_key: string;
  source_json: string;
  prev_hash: string;
  hash: string;
  seq: number;
  currency?: string;
  tags?: string[];
  created_at: string;
  created_by: string;
  cost_center_id?: string;
  cash_box_id?: string;
  /**
   * `true` si el asiento ya fue cruzado contra un extracto bancario.
   * AÃ±adido en la migraciÃ³n v111 (aditivo: ausente === no conciliado).
   */
  is_reconciled?: boolean;
}

export interface InvoiceResolution {
  id: string;
  business_id: string;
  branch_id?: string;
  prefix: string;
  from_number: number;
  to_number: number;
  current_number: number;
  valid_from: string;
  valid_until: string;
  technical_key?: string;
  is_active: boolean;
  tags?: string[];
  created_at: string;
  updated_at?: string;
}

export interface ProductionAssembly {
  id: string;
  business_id: string;
  product_id: string;
  product_name?: string;
  quantity: number;
  raw_materials_json: string;
  shrinkage_rate_bps?: number;
  actual_shrinkage_bps?: number;
  status: AssemblyStatus;
  cost_center_id?: string;
  started_at?: string;
  completed_at?: string;
  tags?: string[];
  is_active: boolean;
  created_at: string;
  created_by?: string;
}

export interface Affiliate {
  id: string;
  business_id?: string;
  referrer_id: string;
  referrer_name?: string;
  referrer_type?: 'ACCOUNTANT' | 'MERCHANT' | 'USER';
  referred_business_id: string;
  commission_bps?: number;
  total_earned?: number;
  pending_payout?: number;
  currency?: string;
  tags?: string[];
  status: AffiliateStatus;
  created_at: string;
  updated_at?: string;
}

export interface FinancialTelemetry {
  id: string;
  business_id: string;
  sector: string;
  bucket_type: TelemetryBucketType;
  metric: string;
  bucket_key?: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  currency?: string;
  tags?: string[];
  timestamp: string;
}

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// 4. MODELOS NUEVOS v110 (Calendario, Conversiones, Lifecycle)
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

/** Tarea/turno de trabajo para empleados (sincronizable con Google Calendar). */
export interface WorkShiftTask {
  id: string;
  business_id: string;
  user_id: string;
  branch_id?: string;
  title: string;
  description?: string;
  location_address?: string;
  start_time: string;
  end_time: string;
  status: WorkShiftStatus;
  tags?: string[];
  created_at: string;
  updated_at?: string;
  google_calendar_event_id?: string;
}

/** Asiento de cierre anual consolidado (compactaciÃ³n de histÃ³rico). */
export interface AnnualClosingEntry {
  id: string;
  business_id: string;
  year: number;
  closing_date: string;
  total_sales: number;
  total_expenses: number;
  total_taxes: number;
  net_income: number;
  records_compacted: number;
  currency?: string;
  tags?: string[];
  created_at: string;
  created_by: string;
}

/** Registro de trabajos de compactaciÃ³n ejecutados. */
export interface LifecycleJob {
  id: string;
  business_id: string;
  job_type: 'ANNUAL_CLOSING' | 'HISTORICAL_ARCHIVE' | 'DATA_CLEANUP';
  status: LifecycleJobStatus;
  started_at: string;
  completed_at?: string;
  records_processed: number;
  error_message?: string;
  metadata_json?: string;
  created_by: string;
}

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// 5. BASE DE DATOS DEXIE NIIZ
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export class NiizDB extends Dexie {
  profiles!: Table<UserProfile>;
  products!: Table<Product>;
  sales!: Table<Sale>;
  sale_items!: Table<SaleItem>;
  cash_shifts!: Table<CashShift>;
  sync_queue!: Table<SyncQueueItem>;
  payment_keys!: Table<PaymentKey>;
  restaurant_tables!: Table<TableConfig>;
  customers!: Table<Customer>;
  order_tickets!: Table<OrderTicket>;
  settings!: Table<AppSettings>;
  /**
   * Llaves de API para contadores externos (migraciÃ³n v112).
   * Solo se persiste el hash SHA-256 y la versiÃ³n enmascarada.
   */
  accounting_api_keys!: Table<BusinessAccountingApiKey>;
  inventory_logs!: Table<InventoryLog>;
  fulfillment_requests!: Table<FulfillmentRequest>;
  budgets!: Table<Budget>;
  recurring_expenses!: Table<RecurringExpense>;
  debts!: Table<Debt>;
  tax_payments!: Table<TaxPayment>;
  services!: Table<Service>;
  resources!: Table<Resource>;
  appointments!: Table<Appointment>;
  property_units!: Table<PropertyUnit>;
  property_bookings!: Table<PropertyBooking>;
  store_orders!: Table<StoreOrder>;
  notifications!: Table<NiizNotification>;
  housekeeping_tasks!: Table<HousekeepingTask>;
  rental_items!: Table<RentalItem>;
  rental_bookings!: Table<RentalBooking>;
  telemetry_events!: Table<TelemetryEvent>;
  flip_transactions!: Table<FlipTransaction>;
  flip_pending_recharges!: Table<FlipPendingRecharge>;
  devices!: Table<Device>;
  employee_sessions!: Table<EmployeeSession>;
  security_queries!: Table<SecurityQuery>;
  subastas!: Table<Subasta>;
  subasta_bids!: Table<SubastaBid>;
  blog_posts!: Table<BlogPost>;
  expenses!: Table<Expense>;
  ai_sessions!: Table<AIChatSession>;
  ai_messages!: Table<AIChatMessage>;
  installed_addons!: Table<InstalledAddon>;
  suppliers!: Table<Supplier>;
  supplier_products!: Table<SupplierProduct>;
  ad_banners!: Table<AdBanner>;
  category_auctions!: Table<CategoryAuction>;
  naranja_plus!: Table<NaranjaPlus>;
  financial_certificates!: Table<FinancialCertificate>;
  staff_changes!: Table<StaffChange>;
  marketplace_orders!: Table<MarketplaceOrder, string>;
  support_requests!: Table<SupportRequest>;
  system_templates!: Table<SystemTemplate, string>;
  documents!: Table<SavedDocument, string>;
  spreadsheets!: Table<SavedSpreadsheet, string>;
  branches!: Table<Branch>;
  cost_centers!: Table<CostCenter>;
  admin_cash_boxes!: Table<AdminCashBox>;
  accounting_entries!: Table<AccountingEntry>;
  invoice_resolutions!: Table<InvoiceResolution>;
  production_assemblies!: Table<ProductionAssembly>;
  affiliate_profiles!: Table<AffiliateProfile>;
  affiliate_invite_codes!: Table<AffiliateInviteCode>;
  affiliate_referrals!: Table<AffiliateReferral>;
  affiliate_commissions!: Table<AffiliateCommission>;
  affiliate_withdrawals!: Table<AffiliateWithdrawal>;
  affiliate_verifications!: Table<AffiliateVerification>;
  conference_rooms!: Table<ConferenceRoom>;
  conference_invites!: Table<ConferenceInvite>;
  auction_entries!: Table<AuctionEntry>;
  financial_telemetry!: Table<FinancialTelemetry>;

  // â”€â”€â”€ NUEVAS TABLAS v110 â”€â”€â”€
  work_shift_tasks!: Table<WorkShiftTask>;
  annual_closing_entries!: Table<AnnualClosingEntry>;
  lifecycle_jobs!: Table<LifecycleJob>;

  // â”€â”€â”€ NUEVAS TABLAS v111 Â· CONCILIACIÃ“N BANCARIA â”€â”€â”€
  bank_reconciliation_sessions!: Table<ReconciliationSession>;
  bank_statement_items!: Table<StoredStatementItem>;
  reconciliation_matches!: Table<StoredMatch>;

  // â”€â”€â”€ NUEVAS TABLAS v113 Â· CONVENIOS / TERCEROS PAGADORES â”€â”€â”€
  third_party_payers!: Table<ThirdPartyPayer>;
  agreement_transactions!: Table<AgreementTransaction>;
  agreement_billing_batches!: Table<AgreementBillingBatch>;

  // â”€â”€â”€ NUEVA TABLA v114 Â· BÃ“VEDA LEGAL / COMPLIANCE â”€â”€â”€
  legal_documents!: Table<LegalDocumentRecord>;

  // â”€â”€â”€ NUEVAS TABLAS v115 Â· MODO PROVEEDOR MAYORISTA NATIVO â”€â”€â”€
  supplier_profiles!: Table<SupplierProfile>;
  b2b_purchase_orders!: Table<B2BPurchaseOrder>;
  subscription_plans!: Table<SubscriptionPlan>;
  customer_subscriptions!: Table<CustomerSubscription>;
  subscription_charges!: Table<SubscriptionCharge>;
  sale_returns!: Table<SaleReturn>;
  sale_return_items!: Table<SaleReturnItem>;
  store_credits!: Table<StoreCredit>;
  tip_settlements!: Table<TipSettlement>;
  blind_count_audits!: Table<BlindCountAudit>;
  blind_count_items!: Table<BlindCountItem>;
  branch_stock!: Table<BranchStock>;
  inventory_transfers!: Table<InventoryTransfer>;
  inventory_transfer_items!: Table<InventoryTransferItem>;
  logistics_jobs!: Table<LogisticsJob>;
  logistics_couriers!: Table<LogisticsCourier>;
  logistics_providers!: Table<LogisticsProviderProfile>;
  logistics_requests!: Table<CourierRequest>;
  assignments!: Table<Assignment>;
  assignment_tasks!: Table<AssignmentTask>;
  incentive_awards!: Table<IncentiveAward>;
  drive_folders!: Table<DriveFolder>;
  drive_files!: Table<DriveFile>;
  patient_document_requests!: Table<PatientDocumentRequest>;
  industrial_sponsors!: Table<IndustrialSponsor>;
  industrial_sponsor_requests!: Table<IndustrialSponsorRequest>;
  ai_learning_events!: Table<AILearningEvent>;
  firm_clients!: Table<FirmClient>;
  firm_closings!: Table<FirmClosing>;

  constructor() {
    super('NiizDB_v8');

    this.version(76).stores({
      profiles: 'id, business_id, email, pin, role, is_active',
      products: 'id, business_id, name, sku, barcode, type, category_id, is_active, default_destination, supabase_id, expiration_date',
      sales: 'id, business_id, created_at, status, synced, payment_method, customer_id, user_id, table_id, [synced+created_at]',
      sale_items: 'id, sale_id, product_id, ticket_destination',
      cash_shifts: 'id, business_id, status, opened_at',
      restaurant_tables: 'id, business_id, name, zone, status, current_sale_id',
      customers: 'id, business_id, full_name, phone, email, current_debt, created_at',
      order_tickets: 'id, sale_id, table_id, destination, status, created_at',
      settings: 'id, business_id',
      inventory_logs: 'id, business_id, product_id, created_at, reason',
      fulfillment_requests: 'id, business_id, status, destination, created_at',
      budgets: 'id, business_id, month',
      recurring_expenses: 'id, business_id, active',
      tax_payments: 'id, business_id, date',
      services: 'id, name, is_active',
      resources: 'id, name, type, is_active',
      appointments: 'id, resource_id, start_time, status, customer_name',
      property_units: 'id, business_id, name, type, status, is_active',
      property_bookings: 'id, business_id, unit_id, customer_id, check_in_date, check_out_date, status',
      housekeeping_tasks: 'id, business_id, unit_id, type, priority, status, due_date',
      rental_items: 'id, business_id, name, category, is_active',
      rental_bookings: 'id, business_id, customer_id, start_datetime, end_datetime, status',
      expenses: 'id, business_id, created_at, category, user_id',
      ai_sessions: 'id, business_id, updated_at',
      ai_messages: 'id, session_id, created_at',
      installed_addons: 'id, business_id, addon_type, is_active',
      store_orders: 'id, business_id, status, created_at, is_read',
      notifications: 'id, business_id, type, created_at, is_read'
    });

    this.version(77).stores({
      suppliers: 'id, whatsapp, is_verified, membership_type',
      supplier_products: 'id, supplier_id, category, is_active',
      marketplace_orders: 'id, supplier_id, business_id, status, payment_status',
      products: 'id, business_id, name, sku, barcode, type, category_id, is_active, stock_min',
      support_requests: 'id, business_id, status, created_at'
    });

    this.version(79).stores({
      suppliers: 'id, whatsapp, is_verified, verification_code, membership_type'
    });

    this.version(80).stores({
      suppliers: 'id, whatsapp, is_verified, verification_code, membership_type, wallet_balance, is_ads'
    });

    this.version(81).stores({
      system_templates: 'id, target_type',
      suppliers: 'id, whatsapp, is_verified, verification_code, membership_type, wallet_balance, is_ads, slug',
      businesses: 'id, owner_id, plan, slug'
    });

    this.version(82).stores({
      telemetry_events: 'id, business_hash, sale_id, occurred_at, uploaded'
    });

    this.version(83).stores({
      settings: 'id, business_id, created_at'
    });

    this.version(84).stores({
      flip_transactions: 'id, type, created_at'
    });

    this.version(85).stores({
      devices: 'id, business_id, name, terminal_type, is_active',
      employee_sessions: 'id, user_id, device_id, business_id, login_at'
    });

    this.version(86).stores({
      security_queries: 'id, entity_name, entity_employee_id, queried_document, queried_at'
    });

    this.version(87).stores({
      subastas: 'id, product_name, status, starts_at',
      subasta_bids: 'id, subasta_id, business_id, created_at'
    });

    this.version(88).stores({
      flip_pending_recharges: 'id, business_id, status, created_at'
    });

    this.version(89).stores({}).upgrade(async (tx) => {
      try {
        const supplierProducts = await tx.table('supplier_products').toArray();
        for (const sp of supplierProducts) {
          const sup = sp as any;
          const existing = await tx.table('products').get(sup.id);
          if (!existing) {
            await tx.table('products').put({
              id: sup.id,
              business_id: sup.supplier_id || 'local',
              name: sup.name,
              description: sup.description || '',
              price: sup.price_unit || 0,
              cost: null,
              stock_current: sup.stock || 0,
              stock_min: 0,
              unit: 'UNIDAD',
              type: 'FISICO',
              is_active: sup.is_active === 1,
              default_destination: 'NONE',
              image_url: sup.image_url || null,
              price_bulk: sup.price_bulk || 0,
              bulk_min_qty: sup.bulk_min_qty || 0,
              commission_type: sup.commission_type || 'PERCENTAGE',
              supplier_category: sup.category || '',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });
          }
        }
      } catch (e) {
        console.warn('MigraciÃ³n v89 (unificaciÃ³n de catÃ¡logo) parcial:', e);
      }
    });

    this.version(90).stores({
      blog_posts: 'id, slug, status, created_at'
    });

    this.version(91).stores({
      debts: 'id, business_id, status, created_at'
    });

    this.version(92).stores({}).upgrade(async (tx) => {
      try {
        await tx.table('products').toCollection().modify((p: any) => {
          if (!p.product_kind) p.product_kind = 'SALE';
        });
      } catch (e) {
        console.warn('MigraciÃ³n v92 (insumos) parcial:', e);
      }
    });

    this.version(93).stores({
      restaurant_tables: 'id, business_id, name, zone, status, current_sale_id, parent_table_id'
    }).upgrade(async (tx) => {
      try {
        const tables = await tx.table('restaurant_tables').toArray();
        for (const t of tables) {
          if (t?.parent_table_id == null) continue;
          const parent = tables.find((x: any) => String(x.id) === String(t.parent_table_id));
          if (!parent) {
            await tx.table('restaurant_tables').update(t.id, { parent_table_id: null, joined_at: null });
          }
        }
      } catch (e) {
        console.warn('MigraciÃ³n v93 (uniÃ³n de mesas) parcial:', e);
      }
    });

    this.version(94).stores({}).upgrade(async (tx) => {
      try {
        await tx.table('sales').toCollection().modify((s: any) => {
          if (!s.payment_mode) s.payment_mode = 'SINGLE';
          if (!Array.isArray(s.diners)) s.diners = [];
          if (!Array.isArray(s.settlements)) s.settlements = [];
        });
      } catch (e) {
        console.warn('MigraciÃ³n v94 (modo de pago/comensales) parcial:', e);
      }
    });

    this.version(95).stores({}).upgrade(async (tx) => {
      try {
        await tx.table('sales').toCollection().modify((s: any) => {
          if (s.separate_invoices === undefined) s.separate_invoices = false;
        });
      } catch (e) {
        console.warn('MigraciÃ³n v95 (factura por aparte) parcial:', e);
      }
    });

    this.version(96).stores({}).upgrade(async (tx) => {
      try {
        await tx.table('sale_items').toCollection().modify((i: any) => {
          if (!i.status) i.status = 'ACTIVE';
        });
        await tx.table('order_tickets').toCollection().modify((t: any) => {
          if (t.cancelled === undefined) t.cancelled = false;
          if (t.partial_cancel === undefined) t.partial_cancel = false;
        });
      } catch (e) {
        console.warn('MigraciÃ³n v96 (cancelaciÃ³n de productos) parcial:', e);
      }
    });

    this.version(97).stores({}).upgrade(async (tx) => {
      try {
        await tx.table('sale_items').toCollection().modify((i: any) => {
          if (i.item_note === undefined) i.item_note = null;
        });
        await tx.table('order_tickets').toCollection().modify((t: any) => {
          if (t.ticket_notes === undefined) t.ticket_notes = null;
        });
      } catch (e) {
        console.warn('MigraciÃ³n v97 (notas de cocina) parcial:', e);
      }
    });

    this.version(98).stores({}).upgrade(async (tx) => {
      try {
        await tx.table('sales').toCollection().modify((s: any) => {
          if (s.cancelled_at === undefined) s.cancelled_at = null;
          if (s.cancel_reason === undefined) s.cancel_reason = null;
          if (s.cancel_reason_label === undefined) s.cancel_reason_label = null;
          if (s.cancel_notes === undefined) s.cancel_notes = null;
          if (s.cancelled_by === undefined) s.cancelled_by = null;
        });
      } catch (e) {
        console.warn('MigraciÃ³n v98 (cancelaciÃ³n de pedido) parcial:', e);
      }
    });

    this.version(99).stores({
      ad_banners: 'id, supplier_id, city, is_active, created_at',
    }).upgrade(async (tx) => {
      try {
        await tx.table('products').toCollection().modify((p: any) => {
          if (p.is_sponsored === undefined) p.is_sponsored = false;
          if (p.sponsored_until === undefined) p.sponsored_until = null;
        });
      } catch (e) {
        console.warn('MigraciÃ³n v99 (patrocinio de productos) parcial:', e);
      }
    });

    this.version(100).stores({
      category_auctions: 'id, category, city, month_year, status, ends_at',
    });

    this.version(101).stores({
      naranja_plus: 'id, business_id, status, expires_at',
    });

    this.version(102).stores({
      financial_certificates: 'id, business_id, verification_token, created_at',
    });

    this.version(103).stores({
      sync_queue: 'id, business_id, entity, status, created_at',
      expenses: 'id, business_id, created_at, category, user_id, synced',
      cash_shifts: 'id, business_id, status, opened_at, synced',
    });

    this.version(104).stores({
      payment_keys: 'id, business_id, bank_name, is_default, is_active',
    }).upgrade(async (tx) => {
      try {
        const settings: any = await tx.table('settings').toCollection().first();
        if (!settings) return;
        const already = await tx.table('payment_keys').count();
        if (already > 0) return;
        const nowIso = new Date().toISOString();
        const rows: PaymentKey[] = [];
        if (settings.bre_b_key) {
          rows.push({
            id: `pk-bre-b-${settings.id || 'local'}`,
            business_id: settings.business_id || 'local',
            bank_name: 'Bre-B',
            key_type: (String(settings.bre_b_key_type || 'CELULAR').toUpperCase() as PaymentKeyType),
            key_value: String(settings.bre_b_key),
            holder_name: String(settings.bre_b_holder || settings.business_name || ''),
            qr_image: '',
            is_default: true,
            is_active: settings.bre_b_enabled !== false,
            created_at: nowIso,
          });
        }
        const legacyAccounts = Array.isArray(settings.bank_accounts) ? settings.bank_accounts : [];
        for (const acc of legacyAccounts) {
          if (!acc) continue;
          rows.push({
            id: String(acc.id || `pk-${Math.random().toString(36).slice(2)}`),
            business_id: settings.business_id || 'local',
            bank_name: String(acc.name || 'Banco'),
            key_type: 'NUMERO_CUENTA',
            key_value: String(acc.details || ''),
            holder_name: String(settings.business_name || ''),
            qr_image: '',
            is_default: rows.length === 0,
            is_active: acc.active !== false,
            created_at: nowIso,
          });
        }
        for (const row of rows) await tx.table('payment_keys').put(row);
      } catch (e) {
        console.warn('MigraciÃ³n v104 (llaves bancarias) parcial:', e);
      }
    });

    this.version(105).stores({
      staff_changes: 'id, business_id, user_id, change_type, effective_at, created_at',
    }).upgrade(async (tx) => {
      try {
        await tx.table('profiles').toCollection().modify((p: any) => {
          if (p.tips_accumulated === undefined) p.tips_accumulated = 0;
          if (p.contract_type === undefined) p.contract_type = null;
        });
      } catch (e) {
        console.warn('MigraciÃ³n v105 (ficha de personal) parcial:', e);
      }
    });

    this.version(106).stores({}).upgrade(async (tx) => {
      try {
        await tx.table('settings').toCollection().modify((s: any) => {
          const current = Number(s?.credit_value_cop ?? s?.coin_value ?? 0);
          if (!Number.isFinite(current) || current !== FLIP_COIN_VALUE_COP) {
            console.log(`ðŸ’± [MIGRACIÃ“N] Flip Coin: ${current || 'sin valor'} â†’ ${FLIP_COIN_VALUE_COP} COP`);
            s.credit_value_cop = FLIP_COIN_VALUE_COP;
          }
          if (s.coin_value !== undefined) s.coin_value = s.credit_value_cop;
        });
      } catch (e) {
        console.warn('MigraciÃ³n v106 (valor del Flip Coin) parcial:', e);
      }
    });

    this.version(107).stores({
      products: 'id, business_id, name, sku, barcode, type, category_id, is_active, default_destination, supabase_id, expiration_date, catalog',
    }).upgrade(async (tx) => {
      try {
        await tx.table('products').toCollection().modify((p: any) => {
          if (!p.catalog) p.catalog = 'GENERAL';
        });
      } catch (e) {
        console.warn('MigraciÃ³n v107 (catÃ¡logos) parcial:', e);
      }
    });

    this.version(108).stores({
      documents: 'id, business_id, name, updated_at, created_at',
      spreadsheets: 'id, business_id, name, updated_at, created_at',
    });

    this.version(109).stores({
      branches: 'id, business_id, name, code, is_main, is_active, created_at, *tags',
      cost_centers: 'id, business_id, branch_id, code, name, default_box, budget, is_active, *tags',
      admin_cash_boxes: 'id, business_id, parent_id, type, name, percentage, balance, status, *tags',
      accounting_entries: 'id, business_id, branch_id, number, date, kind, status, idempotency_key, currency, *tags, [business_id+date]',
      invoice_resolutions: 'id, business_id, branch_id, prefix, from_number, to_number, current_number, valid_until, is_active, *tags',
      production_assemblies: 'id, business_id, product_id, is_active, created_at, *tags',
      affiliates: 'id, referrer_id, referred_business_id, status, created_at, *tags',
      financial_telemetry: 'id, business_id, sector, bucket_type, metric, timestamp, *tags, [bucket_type+metric+timestamp]',
      products: 'id, business_id, name, sku, barcode, type, category_id, is_active, default_destination, supabase_id, expiration_date, catalog, tax_rate, landed_cost, *tags',
      expenses: 'id, business_id, created_at, category, user_id, synced, cost_center_id, branch_id, source_box, *tags',
    }).upgrade(async (tx) => {
      try {
        const settings = await tx.table('settings').toCollection().first() as any;
        const businessId = settings?.business_id || 'local';
        const businessName = settings?.business_name || 'Mi Negocio';
        const nowIso = new Date().toISOString();

        const mainBranchExists = await tx.table('branches')
          .where('business_id').equals(businessId)
          .and((b: any) => b.is_main === true)
          .count();
        if (mainBranchExists === 0) {
          await tx.table('branches').add({
            id: `br_${businessId}_main`,
            business_id: businessId,
            name: businessName || 'Sede Principal',
            code: 'MAIN',
            is_main: true,
            is_active: true,
            created_at: nowIso,
            tags: ['principal'],
          });
        }

        const boxConfigs = [
          { role: 'OPERATING', name: 'Caja OperaciÃ³n', percentage: 50, canBeDonor: true },
          { role: 'REINVESTMENT', name: 'Caja ReinversiÃ³n', percentage: 20, canBeDonor: true },
          { role: 'TAX', name: 'Caja Impuestos', percentage: 15, canBeDonor: false },
          { role: 'PROFIT', name: 'Caja Ganancias', percentage: 15, canBeDonor: true },
        ] as const;

        for (const cfg of boxConfigs) {
          const boxExists = await tx.table('admin_cash_boxes')
            .where('business_id').equals(businessId)
            .and((b: any) => b.role === cfg.role && b.type === 'MASTER')
            .count();
          if (boxExists === 0) {
            await tx.table('admin_cash_boxes').add({
              id: `box_${businessId}_${cfg.role.toLowerCase()}`,
              business_id: businessId,
              type: 'MASTER',
              role: cfg.role,
              name: cfg.name,
              percentage: cfg.percentage,
              balance: 0,
              floor: 0,
              can_donate_to: [],
              status: 'ACTIVE',
              created_at: nowIso,
              tags: ['master', cfg.role.toLowerCase()],
            });
          }
        }

        const resExists = await tx.table('invoice_resolutions')
          .where('business_id').equals(businessId)
          .and((r: any) => r.is_active === true)
          .count();
        if (resExists === 0) {
          const today = nowIso.slice(0, 10);
          await tx.table('invoice_resolutions').add({
            id: `res_${businessId}_default`,
            business_id: businessId,
            prefix: 'FAC',
            from_number: 1,
            to_number: 999999,
            current_number: 0,
            valid_from: today,
            valid_until: '2030-12-31',
            is_active: true,
            created_at: nowIso,
            tags: ['dian', 'default'],
          });
        }

        await tx.table('settings').toCollection().modify((s: any) => {
          if (!s.currency_code) s.currency_code = 'COP';
          if (!s.currency_symbol_display) s.currency_symbol_display = '$';
          if (!s.decimal_separator) s.decimal_separator = ',';
          if (!s.thousand_separator) s.thousand_separator = '.';
        });
      } catch (e) {
        console.warn('MigraciÃ³n v109 (auto-reparaciÃ³n financiera) parcial:', e);
      }
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 110: CALENDARIO DE PERSONAL + CONVERSIONES + LIFECYCLE
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(110).stores({
      work_shift_tasks: 'id, business_id, user_id, branch_id, start_time, status, *tags',
      annual_closing_entries: 'id, business_id, year, closing_date, *tags',
      lifecycle_jobs: 'id, business_id, job_type, status, started_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 111: CONCILIACIÃ“N BANCARIA
    // Aditiva: solo crea tablas nuevas. No toca datos existentes.
    // `accounting_entries.is_reconciled` es una columna lÃ³gica nueva (opcional,
    // sin Ã­ndice propio) â†’ no requiere reindexar y su ausencia equivale a false.
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(111).stores({
      bank_reconciliation_sessions:
        'id, business_id, bank_account_id, status, statement_period_to',
      bank_statement_items: 'id, session_id, date, status',
      reconciliation_matches: 'id, session_id, statement_item_id, system_entry_id',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 112: GESTIÃ“N DE ACCESO API PARA CONTADOR EXTERNO
    // Aditiva: solo crea la tabla. No toca datos existentes.
    // `apiKeyHash` es SHA-256: la clave en claro nunca llega a la base.
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(112).stores({
      accounting_api_keys: 'id, business_id, isActive, createdAt, revokedAt',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 113: CONVENIOS / CRÃ‰DITOS FINANCIADOS / TERCEROS PAGADORES
    // Aditiva: solo crea tablas nuevas. No toca datos ni Ã­ndices existentes.
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(113).stores({
      third_party_payers: 'id, business_id, payer_type, is_active, nit, *tags',
      agreement_transactions:
        'id, business_id, third_party_payer_id, beneficiary_customer_id, sale_id, status, batch_id, created_at',
      agreement_billing_batches:
        'id, business_id, third_party_payer_id, batch_number, status, period_from, period_to, created_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 114: BÃ“VEDA LEGAL, PÃ“LIZAS Y CUMPLIMIENTO CONTRACTUAL
    // Aditiva: solo crea la tabla. Los dÃ­as restantes y el semÃ¡foro se
    // recalculan al vuelo al leer (no dependen de un job nocturno).
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(114).stores({
      legal_documents:
        'id, business_id, branch_id, document_type, policy_type, valid_until, status, is_active, *tags',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 115: MODO PROVEEDOR MAYORISTA NATIVO (B2B)
    // Aditiva: crea perfil y Ã³rdenes. El catÃ¡logo NO se duplica: usa
    // `products` con `is_b2b_wholesale`, `wholesale_price` y
    // `min_wholesale_qty` (campos opcionales no indexados).
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(115).stores({
      supplier_profiles: 'id, business_id, is_supplier_active, is_verified, city',
      b2b_purchase_orders:
        'id, order_number, buyer_business_id, seller_business_id, status, created_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 116: reafirma las tablas B2B si una instalaciÃ³n quedÃ³ con el
    // esquema 115 incompleto (evita DexieError2 al abrir Modo Proveedor).
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(116).stores({
      supplier_profiles: 'id, business_id, is_supplier_active, is_verified, city',
      b2b_purchase_orders:
        'id, order_number, buyer_business_id, seller_business_id, status, created_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 117: SUSCRIPCIONES DE SERVICIOS
    // Planes (internet, plan mÃ³vil, plataformas) + asignaciones a clientes +
    // historial de cobros. No duplica inventario: es un servicio recurrente.
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(117).stores({
      subscription_plans: 'id, business_id, is_active, created_at',
      customer_subscriptions:
        'id, business_id, customer_id, plan_id, status, next_charge_date, created_at',
      subscription_charges: 'id, business_id, subscription_id, customer_id, paid_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 118: DEVOLUCIONES, CAMBIOS Y BONOS A FAVOR
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(118).stores({
      sale_returns: 'id, business_id, sale_id, customer_id, type, created_at',
      sale_return_items: 'id, return_id, product_id, direction',
      store_credits: 'id, business_id, customer_id, code, status, created_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 119: LIQUIDACIÃ“N DE PROPINAS DEL PERIODO
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(119).stores({
      tip_settlements: 'id, business_id, created_at, period_to',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 120: AUDITORÃA DE INVENTARIO A CIEGAS (BLIND COUNT)
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(120).stores({
      blind_count_audits: 'id, business_id, status, started_at, completed_at',
      blind_count_items: 'id, audit_id, business_id, product_id',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 121: SUCURSALES, STOCK POR SEDE Y TRANSFERENCIAS
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(121).stores({
      branch_stock: 'id, business_id, branch_id, product_id',
      inventory_transfers:
        'id, business_id, from_branch_id, to_branch_id, status, created_at',
      inventory_transfer_items: 'id, transfer_id, product_id',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 122: LOGÃSTICA, DOMICILIOS Y TRASTEOS
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(122).stores({
      logistics_jobs: 'id, business_id, service_type, status, service_date, created_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 123: DOMICILIARIOS / TRANSPORTISTAS
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(123).stores({
      logistics_couriers: 'id, business_id, phone, plate, is_active, created_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 124: ÃNDICES DE PORTAL Y DESPACHOS POR DOMICILIARIO
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(124).stores({
      logistics_jobs:
        'id, business_id, service_type, status, service_date, courier_id, created_at',
      logistics_couriers:
        'id, business_id, phone, plate, portal_code, is_active, created_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 125: MARKETPLACE DE DOMICILIOS ENTRE NEGOCIOS
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(125).stores({
      logistics_providers: 'id, business_id, is_available, city, created_at',
      logistics_requests:
        'id, requester_business_id, provider_business_id, status, created_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 126: ASIGNACIONES DUEÃ‘O â†’ GERENTE â†’ EMPLEADOS
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(126).stores({
      assignments: 'id, business_id, status, category, created_at, created_by',
      assignment_tasks:
        'id, assignment_id, business_id, employee_id, status, created_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 127: INCENTIVOS PARA TRABAJADORES
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(127).stores({
      incentive_awards: 'id, business_id, employee_id, period, created_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 128: NIIZ DRIVE DE DOCUMENTOS (CARPETAS Y ARCHIVOS)
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(128).stores({
      drive_folders: 'id, business_id, parent_id, name, created_at',
      drive_files: 'id, business_id, folder_id, name, mime_type, created_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 129: PORTAL PÃšBLICO DE PACIENTES
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(129).stores({
      patient_document_requests:
        'id, business_id, customer_id, status, created_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 130: PATROCINIOS INTERNOS DE VELAS INDUSTRIALES
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(130).stores({
      industrial_sponsors: 'id, product_name, brand_name, category, is_active, created_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 131: SOLICITUDES DE PATROCINIO INDUSTRIAL
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(131).stores({
      industrial_sponsor_requests: 'id, status, category, created_at',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 132: APRENDIZAJE CONTINUO DE LA IA (FEEDBACK ANÃ“NIMO)
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(132).stores({
      ai_learning_events: 'id, business_hash, rating, created_at, synced',
    });

    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // VERSIÃ“N 133: ZANAHORIA STUDIO Â· FIRMAS CONTABLES
    // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    this.version(133).stores({
      firm_clients: 'id, firm_business_id, client_business_id, nit, status, created_at',
      firm_closings: 'id, firm_business_id, client_id, period, status, created_at',
    });

    // ─────────────────────────────────────────────────────────────────────
    // VERSIÓN 134: AFILIADOS, COMISIONES, RETIROS Y VERIFICACIONES
    // ─────────────────────────────────────────────────────────────────────
    this.version(134).stores({
      affiliate_profiles: 'id, email, affiliate_code, affiliate_type, status, created_at',
      affiliate_invite_codes: 'id, affiliate_id, code, created_at',
      affiliate_referrals: 'id, affiliate_id, invite_code, business_id, active, expires_at',
      affiliate_commissions: 'id, affiliate_id, referral_id, business_id, created_at',
      affiliate_withdrawals: 'id, affiliate_id, status, requested_at',
      affiliate_verifications: 'id, affiliate_id, code, status, created_at',
    });

    // ─────────────────────────────────────────────────────────────────────
    // VERSIÓN 135: CONFERENCIAS P2P (BETA)
    // ─────────────────────────────────────────────────────────────────────
    this.version(135).stores({
      conference_rooms: 'id, code, host_user_id, business_id, created_at, expires_at',
      conference_invites: 'id, room_code, target_user_id, created_at',
    });

    // ─────────────────────────────────────────────────────────────────────
    // VERSIÓN 136: ENTRADAS PAGADAS A SUBASTAS (100 COINS)
    // ─────────────────────────────────────────────────────────────────────
    this.version(136).stores({
      auction_entries: 'id, auction_id, user_id, paid_at',
    });
  }

  async initializeTemplates() {
    await this.seedMarketplace();
    const templates: SystemTemplate[] = [
      { id: 'template-default', name: 'Plantilla ClÃ¡sica', description: 'El diseÃ±o oficial y confiable de NIIZ. Gratuita para siempre.', price: 0, target_type: 'BUSINESS' },
      { id: 'template-minimal', name: 'Plantilla Minimal', description: 'DiseÃ±o limpio y moderno, ideal para tiendas que priorizan el producto. Gratuita.', price: 0, target_type: 'BUSINESS' },
      { id: 'template-dark-b2b', name: 'Plantilla Dark Pro', description: 'Estilo nocturno agresivo que resalta tu catÃ¡logo. Compra Ãºnica de 120 Flip.', price: 120, target_type: 'BUSINESS' },
      { id: 'template-express', name: 'Plantilla Tienda Express', description: 'Optimizada para conversiones rÃ¡pidas en tu enlace de venta. Compra Ãºnica de 120 Flip.', price: 120, target_type: 'BUSINESS' },
      { id: 'template-showcase', name: 'Plantilla Showcase', description: 'Vitrina premium para destacar productos con fotos grandes. Compra Ãºnica de 120 Flip.', price: 120, target_type: 'BUSINESS' },
      { id: 'template-dark-b2b-supplier', name: 'Plantilla CatÃ¡logo Proveedor', description: 'DiseÃ±o nocturno para catÃ¡logos de proveedores B2B.', price: 120, target_type: 'SUPPLIER' },
    ];
    for (const t of templates) {
      const exists = await this.system_templates.get(t.id);
      if (!exists) {
        await this.system_templates.add(t as SystemTemplate);
      } else {
        await this.system_templates.update(t.id, { price: t.price, description: t.description, name: t.name });
      }
    }
  }

  async seedMarketplace() {
    try {
      const demoIds = ['sup-1', 'sup-2', 'sup-3'];
      await this.supplier_products.where('supplier_id').anyOf(demoIds).delete();
      await this.suppliers.bulkDelete(demoIds);
    } catch (e) {
      void e;
    }
  }

  async getOpenShift() {
    return await this.cash_shifts.where({ status: 'OPEN' }).first();
  }

  async createSaleWithItems(sale: Omit<Sale, 'id'>, items: Omit<SaleItem, 'id' | 'sale_id'>[]) {
    return await this.transaction('rw', this.sales, this.sale_items, async () => {
      const saleId = await this.sales.add(sale as Sale);
      const itemsWithSale = items.map(item => ({ ...item, sale_id: saleId as string }));
      await this.sale_items.bulkAdd(itemsWithSale as SaleItem[]);
      return saleId;
    });
  }

  /**
   * Reconcilia la secuencia FAC con la resoluciÃ³n DIAN activa y las ventas
   * histÃ³ricas. Se llama al iniciar sesiÃ³n para que el primer cobro no repita
   * nÃºmeros ya usados.
   */
  async initializeInvoiceSequence(businessId: string): Promise<void> {
    const bid = String(businessId || 'local');
    try {
      const resolutions = await this.invoice_resolutions
        .where('business_id')
        .equals(bid)
        .toArray();
      const active = resolutions.filter((row) => row.is_active);
      const resolution =
        active.find((row) => String(row.prefix || '').toUpperCase().replace(/[^A-Z0-9]/g, '') === 'FAC') ??
        active[0];
      const recent = await this.sales.where('business_id').equals(bid).reverse().limit(5000).toArray();
      const maxUsed = recent.reduce(
        (max, sale) => Math.max(max, invoiceNumberValue(sale.invoice_number)),
        0,
      );
      const resolutionLast = Math.max(0, Number(resolution?.current_number || 1) - 1);
      const rangeStart = Math.max(0, Number(resolution?.from_number || 1) - 1);
      initInvoiceSequence({
        businessId: bid,
        prefix: resolution?.prefix || 'FAC',
        padding: 4,
        last: Math.max(maxUsed, resolutionLast, rangeStart, 0),
        ...(resolution?.id ? { resolutionId: resolution.id } : {}),
        toNumber: Math.max(0, Number(resolution?.to_number || 0)),
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.warn('No se pudo inicializar la secuencia de facturas:', error);
    }
  }

  async dangerousReset() {
    if (import.meta.env.DEV || window.confirm('Â¿Resetear toda la base de datos local? Esta acciÃ³n es irreversible.')) {
      await this.delete();
      location.reload();
    }
  }
}

export const db = new NiizDB();

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// FACTURA ÃšNICA (FAC-0001) PARA TODAS LAS PLATAFORMAS
// Los hooks asignan el nÃºmero sin importar si la venta nace en POS, restaurante,
// hotel, alquiler, suscripciones, convenios o venta rÃ¡pida.
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const persistInvoiceCounter = (
  businessId: string,
  raw: number,
  resolutionId?: string,
): void => {
  setTimeout(() => {
    void (async () => {
      try {
        const row = resolutionId
          ? await db.invoice_resolutions.get(resolutionId)
          : await db.invoice_resolutions.where('business_id').equals(businessId).first();
        if (!row?.id) return;
        await db.invoice_resolutions.update(row.id, {
          current_number: Math.max(Number(row.current_number) || 0, raw),
          updated_at: new Date().toISOString(),
        });
      } catch {
        /* el contador local ya quedÃ³ reservado */
      }
    })();
  }, 0);
};

const assignInvoiceToSale = (sale: Sale, target?: Partial<Sale>): void => {
  const currentInvoice = target?.invoice_number ?? sale.invoice_number;
  if (currentInvoice) return;
  const invoice = reserveInvoiceNumber(sale.business_id || 'local');
  if (target) {
    target.invoice_number = invoice.number;
    if (invoice.resolutionId) target.invoice_resolution_id = invoice.resolutionId;
  } else {
    sale.invoice_number = invoice.number;
    if (invoice.resolutionId) sale.invoice_resolution_id = invoice.resolutionId;
  }
  persistInvoiceCounter(
    sale.business_id || 'local',
    invoice.raw,
    invoice.resolutionId,
  );
};

db.sales.hook('creating', (_primaryKey, sale) => {
  // Las cuentas de mesa abiertas (PENDING) no consumen consecutivo.
  if (sale.status === 'COMPLETED') assignInvoiceToSale(sale);
});

db.sales.hook('updating', (modifications, _primaryKey, sale) => {
  const target = modifications as Partial<Sale>;
  const nextStatus = target.status ?? sale.status;
  if (nextStatus === 'COMPLETED') assignInvoiceToSale(sale, target);
});